"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useGeocodingSearch = void 0;
var _environment = require("@ori/environment");
var _fetching = require("@ori/fetching");
var _react = require("react");
var _maps = require("../constants/maps");
var _swrConfig = require("../constants/swrConfig");
var _logger = require("../logger");
var _models = require("../models");
var _usePickupPointsSearchConfiguration = require("./usePickupPointsSearchConfiguration");
var _useUrlOptions = require("./useUrlOptions");
function _interopRequireWildcard(e, t) {
  if ("function" == typeof WeakMap) var r = new WeakMap(),
    n = new WeakMap();
  return (_interopRequireWildcard = function (e, t) {
    if (!t && e && e.__esModule) return e;
    var o,
      i,
      f = {
        __proto__: null,
        default: e
      };
    if (null === e || "object" != typeof e && "function" != typeof e) return f;
    if (o = t ? n : r) {
      if (o.has(e)) return o.get(e);
      o.set(e, f);
    }
    for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]);
    return f;
  })(e, t);
} /* eslint-disable no-param-reassign -- x */
const hookName = 'useGeocodingSearch';
const getFetcherModule = async (mapType, isFetcherModuleLoading, setFetcherModule) => {
  (0, _logger.logDebug)(`${hookName}: Loading geocoding search implementation module for map type ${mapType} ...`);
  let promise;
  switch (mapType) {
    case _models.MapProviderType.Bing:
      promise = Promise.resolve().then(() => _interopRequireWildcard(require('../utils/bingGeocodingSearch')));
      break;
    case _models.MapProviderType.Yandex:
      promise = Promise.resolve().then(() => _interopRequireWildcard(require('../utils/yandexGeocodingSearch')));
      break;
    case _models.MapProviderType.Google:
      promise = Promise.resolve().then(() => _interopRequireWildcard(require('../utils/googleGeocodingSearch')));
      break;
    case _models.MapProviderType.Azure:
      promise = Promise.resolve().then(() => _interopRequireWildcard(require('../utils/azureGeocodingSearch')));
      break;
    default:
      throw new Error(`${hookName}: Wrong map type '${mapType}'.`);
  }
  void promise.then(module => {
    setFetcherModule(module);
    isFetcherModuleLoading.current = false;
  });
  return promise;
};
function isPositionCorrect(position) {
  if (!position) {
    return false;
  }
  const {
    latitude,
    longitude
  } = position;
  if (latitude > 90 || latitude < -90 || longitude > 180 || longitude < -180) {
    return false;
  }
  return true;
}
function getResultsData(result) {
  const {
    results = []
  } = result ?? {};
  const {
    position,
    label: foundPlaceLabel
  } = results[0] ?? {};
  const foundPosition = isPositionCorrect(position) ? position : undefined;
  return {
    foundPosition,
    foundPlaceLabel,
    results
  };
}
const useGeocodingSearch = ({
  isActive,
  maxResultsCount = 1,
  onSuccess,
  searchTerm
}) => {
  if (isActive) {
    (0, _logger.logDebug)(`${hookName} hook called. Params:`, {
      isActive,
      maxResultsCount,
      searchTerm
    });
  }
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const [fetcherModule, setFetcherModule] = (0, _react.useState)();
  const isFetcherModuleLoading = (0, _react.useRef)(false);
  const pickupPointsSearchConfiguration = (0, _usePickupPointsSearchConfiguration.usePickupPointsSearchConfiguration)();
  const {
    mapConfiguration: {
      account: mapAccount = {},
      provider: mapType = undefined
    } = {},
    viewSettings,
    setViewSettings
  } = pickupPointsSearchConfiguration;
  const isGeocodingMapProvider = mapType === undefined ? false : _maps.SUPPORTED_GEOCODING_PROVIDERS.includes(mapType);
  const isActiveQuery = isGeocodingMapProvider && isActive;
  if (isActiveQuery && mapType !== undefined && !fetcherModule && !isFetcherModuleLoading.current) {
    isFetcherModuleLoading.current = true;
    (0, _logger.logDebug)(`${hookName} hook: Supported map provider found, data fetcher has to be loaded.`);
    void getFetcherModule(mapType, isFetcherModuleLoading, setFetcherModule);
  }
  const {
    basePath = '',
    fetchData: fetcher,
    getQueryParams,
    host
  } = fetcherModule ?? {};
  const params = getQueryParams ? getQueryParams(searchTerm, {
    mapAccount,
    tenant,
    maxResultsCount
  }) : {};
  const {
    cacheKeys,
    urlOptions
  } = (0, _useUrlOptions.useUrlOptions)(basePath, params, host);
  const {
    data,
    error: geocodingError,
    isValidating
  } = (0, _fetching.useSWR)(() => isActiveQuery ? cacheKeys : null, async () => fetcher?.(urlOptions), {
    ..._swrConfig.errorHandlingSwrOptions,
    onSuccess: result => {
      (0, _logger.logDebug)(`${hookName} finished successfully. Loaded data:`, result);
      const resultData = getResultsData(result);
      onSuccess?.(resultData);
      const {
        latitude,
        longitude
      } = resultData.foundPosition ?? {};
      if (latitude !== undefined && longitude !== undefined) {
        setViewSettings({
          location: {
            latitude,
            longitude
          },
          zoom: viewSettings.zoom
        });
      }
    }
  });
  const isGeocodingLoading = isFetcherModuleLoading.current || isValidating;
  return {
    geocodingError,
    isGeocodingLoading,
    ...getResultsData(data)
  };
};
exports.useGeocodingSearch = useGeocodingSearch;